/**
 * cloud-page.js — Sanity integration for the Cloud V4 page layout
 *
 * Responsibilities:
 *  - Fetch the cloudPage doc with slug "cloud" from Sanity
 *  - Show/hide sections via show* boolean flags
 *  - Inject content into data-cl4 elements and re-render dynamic sections
 *  - Add Sanity edit-mode buttons (getSanityEditLink) in each section
 *  - Manage navbar scroll transparency and arc stage animation
 */

import { client, urlFor, getSanityEditLink, resolveInternalLink } from './sanity-client';

// ── GROQ query ─────────────────────────────────────────────────────────────────
const QUERY = `*[_type == "cloudPage" && slug.current == "cloud"][0]{
  _id, title, slug,
  showHero, showProductDev, showConsulting, showCaseStudies, showCta,
  heroDescription,
  heroCapabilities[]{ label, description },
  svcHeading, svcLede,
  svcCards[]{ _key, title, description, tags },
  consultingSection{
    heading, lede,
    arcCards[]{ _key, indexLabel, heading, stackList[]{ _key, key, value } }
  },
  "caseStudies": caseStudies[@->visibility != false]->{ _id, title, "slug": slug.current, image, description },
  ctaHeadline, ctaLede, ctaLocations
}`;

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Parse *asterisk* spans into <em> with teal accent */
function parseAccent(text) {
  if (!text) return '';
  return text.replace(/\*([^*]+)\*/g, '<em style="font-style:normal;color:var(--cl4-teal);font-weight:700">$1</em>');
}

/** Set text/innerHTML on a data-cl4 element, guarding against missing nodes */
function setField(attr, value, html = false) {
  const el = document.querySelector(`[data-cl4="${attr}"]`);
  if (!el || !value) return;
  if (html) el.innerHTML = value;
  else el.textContent = value;
}

/** Show or hide a section by ID */
function setVisible(id, visible) {
  const el = document.getElementById(id);
  if (!el) return;
  el.style.display = visible === false ? 'none' : '';
}

// ── Section renderers ──────────────────────────────────────────────────────────

function renderHero(data, docId) {
  const titleEl = document.querySelector('[data-cl4="title"]');
  if (titleEl && data.title) {
    titleEl.innerHTML = `${data.title}<span class="dot">.</span>`;
  }

  setField('heroDescription', data.heroDescription);

  // Capability badges
  if (data.heroCapabilities && data.heroCapabilities.length) {
    const badgesEl = document.getElementById('cl4-hero-badges');
    if (badgesEl) {
      badgesEl.innerHTML = data.heroCapabilities.map((cap, i) => `
        <div class="cl4-badge" data-reveal style="--rd:${i * 80}ms">
          <span class="cl4-badge-dot"></span>
          <div class="cl4-badge-inner">
            <span class="cl4-badge-label">${cap.label || ''}</span>
            <span class="cl4-badge-desc">${cap.description || ''}</span>
          </div>
        </div>`).join('');
      attachRevealObserver(badgesEl.querySelectorAll('[data-reveal]'));
    }
  }

  const heroSection = document.getElementById('cl4-hero');
  if (heroSection && docId) {
    heroSection.classList.add('position-relative');
    heroSection.insertAdjacentHTML('afterbegin',
      getSanityEditLink(docId, 'cloudPage', 'heroDescription', 'Edit Hero'));
  }
}

function renderProductDev(data, docId) {
  if (data.svcHeading) {
    const el = document.querySelector('[data-cl4="svcHeading"]');
    if (el) el.innerHTML = parseAccent(data.svcHeading);
  }
  setField('svcLede', data.svcLede);

  const grid = document.getElementById('cl4-svc-grid');
  if (grid && data.svcCards && data.svcCards.length) {
    grid.innerHTML = data.svcCards.map((card, i) => {
      const idx = String(i + 1).padStart(2, '0');
      const tagsHtml = (card.tags && card.tags.length)
        ? `<div class="cl4-svc-tags">${card.tags.map(t => `<span class="cl4-svc-tag">${t}</span>`).join('')}</div>`
        : '';
      const decoSvg = i === 0
        ? `<svg class="cl4-svc-deco" viewBox="0 0 200 200" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true">
             <circle cx="100" cy="36" r="10"/><circle cx="52" cy="110" r="10"/><circle cx="148" cy="110" r="10"/>
             <circle cx="28" cy="168" r="6"/><circle cx="100" cy="168" r="6"/><circle cx="172" cy="168" r="6"/>
             <line x1="100" y1="46" x2="52" y2="100"/><line x1="100" y1="46" x2="148" y2="100"/>
             <line x1="52" y1="120" x2="28" y2="162"/><line x1="52" y1="120" x2="100" y2="162"/>
             <line x1="148" y1="120" x2="100" y2="162"/><line x1="148" y1="120" x2="172" y2="162"/>
           </svg>`
        : `<svg class="cl4-svc-deco" viewBox="0 0 200 200" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true">
             <circle cx="100" cy="100" r="32"/><circle cx="32" cy="56" r="18"/><circle cx="168" cy="56" r="18"/>
             <circle cx="32" cy="144" r="18"/><circle cx="168" cy="144" r="18"/>
             <line x1="130" y1="80" x2="150" y2="63"/><line x1="70" y1="80" x2="50" y2="63"/>
             <line x1="130" y1="120" x2="150" y2="137"/><line x1="70" y1="120" x2="50" y2="137"/>
           </svg>`;
      const editBtn = docId
        ? getSanityEditLink(docId, 'cloudPage',
            card._key ? `svcCards[_key=="${card._key}"]` : `svcCards[${i}]`,
            `Edit Card: ${card.title || idx}`)
        : '';

      return `
        <article class="cl4-svc" data-reveal style="--rd:${i * 120}ms">
          ${editBtn ? `<div style="position:absolute;top:1rem;right:1rem;z-index:10">${editBtn}</div>` : ''}
          <span class="cl4-svc-num">${idx}</span>
          <h3>${card.title || ''}</h3>
          <p>${card.description || ''}</p>
          ${tagsHtml}
          ${decoSvg}
        </article>`;
    }).join('');
    attachRevealObserver(grid.querySelectorAll('[data-reveal]'));
  }

  const pdSection = document.getElementById('cl4-product-dev');
  if (pdSection && docId) {
    pdSection.classList.add('position-relative');
    pdSection.insertAdjacentHTML('afterbegin',
      getSanityEditLink(docId, 'cloudPage', 'svcHeading', 'Edit Product Dev'));
  }
}

function renderConsulting(consulting, docId) {
  if (!consulting) return;

  if (consulting.heading) {
    const el = document.querySelector('[data-cl4="consultingHeading"]');
    if (el) el.innerHTML = parseAccent(consulting.heading);
  }
  setField('consultingLede', consulting.lede);

  const cardsEl = document.getElementById('cl4-arc-cards');
  if (cardsEl && consulting.arcCards && consulting.arcCards.length) {
    cardsEl.innerHTML = consulting.arcCards.map((card, i) => {
      const ghostNum = String(i + 1).padStart(2, '0');
      const stackHtml = (card.stackList && card.stackList.length)
        ? `<ul class="stack-list">
            ${card.stackList.map(item =>
              `<li><span class="k">${item.key || ''}</span><span class="v">${item.value || ''}</span></li>`
            ).join('')}
           </ul>`
        : '';
      const editBtn = docId
        ? getSanityEditLink(docId, 'cloudPage',
            card._key
              ? `consultingSection.arcCards[_key=="${card._key}"]`
              : `consultingSection.arcCards[${i}]`,
            `Edit Consulting Card: ${card.indexLabel || i + 1}`)
        : '';

      return `
        <article class="cl4-arc-card" data-reveal style="--rd:${i * 120}ms">
          <span class="ghost-num" aria-hidden="true">${ghostNum}</span>
          ${editBtn ? `<div style="position:absolute;top:1rem;right:1rem;z-index:10">${editBtn}</div>` : ''}
          <span class="index">${card.indexLabel || ''}</span>
          <h3>${card.heading || ''}</h3>
          ${stackHtml}
        </article>`;
    }).join('');
    attachRevealObserver(cardsEl.querySelectorAll('[data-reveal]'));
  }

  const consultingSection = document.getElementById('cl4-consulting');
  if (consultingSection && docId) {
    consultingSection.classList.add('position-relative');
    consultingSection.insertAdjacentHTML('afterbegin',
      getSanityEditLink(docId, 'cloudPage', 'consultingSection', 'Edit Consulting'));
  }
}

function renderCaseStudies(caseStudies, docId) {
  const section = document.getElementById('cl4-case-studies');
  if (section && docId) {
    section.classList.add('position-relative');
    section.insertAdjacentHTML('afterbegin',
      getSanityEditLink(docId, 'cloudPage', 'caseStudies', 'Edit Case Studies'));
  }

  const grid = document.getElementById('cl4-cs-grid');
  if (!grid || !caseStudies || !caseStudies.length) return;

  const artBgs = [
    '',
    `radial-gradient(120% 80% at 10% 90%, rgba(229,50,45,.22), transparent 55%),
     radial-gradient(60% 60% at 90% 10%, rgba(31,184,166,.18), transparent 60%),
     linear-gradient(135deg, #0F1114 0%, #14181E 60%, #0A0B0D 100%)`,
    `radial-gradient(120% 80% at 90% 20%, rgba(31,184,166,.3), transparent 55%),
     radial-gradient(60% 60% at 10% 90%, rgba(229,50,45,.15), transparent 60%),
     linear-gradient(135deg, #0F1114 0%, #0D1A18 60%, #0A0B0D 100%)`,
  ];

  const glyphs = [
    `<svg viewBox="0 0 200 200" fill="none" stroke="currentColor" stroke-width="1.4">
       <path d="M100 14 L172 44 V104 C172 144 140 174 100 188 C60 174 28 144 28 104 V44 Z"/>
       <rect x="74" y="92" width="52" height="44" rx="3"/>
       <path d="M84 92 V78 a16 16 0 0 1 32 0 V92"/>
       <circle cx="100" cy="114" r="5" fill="currentColor"/>
     </svg>`,
    `<svg viewBox="0 0 200 200" fill="none" stroke="currentColor" stroke-width="1.4">
       <circle cx="64" cy="100" r="26"/>
       <path d="M86 100 H170"/>
       <path d="M150 100 V120"/><path d="M132 100 V116"/><path d="M170 100 V124"/>
       <circle cx="64" cy="100" r="6" fill="currentColor"/>
     </svg>`,
    `<svg viewBox="0 0 200 200" fill="none" stroke="currentColor" stroke-width="1.4">
       <rect x="40" y="60" width="120" height="80" rx="6"/>
       <path d="M70 80 H130 M70 100 H110 M70 120 H120"/>
       <circle cx="155" cy="65" r="14" fill="currentColor" opacity=".3"/>
       <path d="M148 65 L153 70 L162 60" stroke="#fff" stroke-width="2" fill="none"/>
     </svg>`,
  ];

  grid.innerHTML = caseStudies.map((cs, i) => {
    const href = cs.title
      ? resolveInternalLink(`/case-studies/case-study.html?title=${encodeURIComponent(cs.title)}`)
      : '#';
    const hasImage = cs.image && cs.image.asset;
    const imgUrl = hasImage ? urlFor(cs.image).width(800).quality(80).url() : '';
    const artStyle = hasImage
      ? `style="background-image:linear-gradient(180deg, rgba(0,0,0,.15), rgba(0,0,0,.65)), url('${imgUrl}');background-size:cover;background-position:center"`
      : (artBgs[i] ? `style="background:${artBgs[i]}"` : '');
    const glyph = glyphs[i % glyphs.length];

    return `
      <a href="${href}" class="cl4-cs-card" data-reveal style="--rd:${i * 100}ms">
        <div class="art" ${artStyle}>
          <div class="art-grid"></div>
          ${hasImage ? '' : `<div class="art-glyph">${glyph}</div>`}
        </div>
        <div class="read" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M5 12h14M13 5l7 7-7 7"/>
          </svg>
        </div>
        <span class="ribbon"></span>
        <div class="meta">
          <span class="cs-tag">Cloud</span>
          <h3>${cs.title || ''}</h3>
        </div>
      </a>`;
  }).join('');

  attachRevealObserver(grid.querySelectorAll('[data-reveal]'));
}

function renderCta(data, docId) {
  if (data.ctaHeadline) {
    const el = document.querySelector('[data-cl4="ctaHeadline"]');
    if (el) el.innerHTML = parseAccent(data.ctaHeadline);
  }
  setField('ctaLede',      data.ctaLede);
  setField('ctaLocations', data.ctaLocations);

  const ctaSection = document.getElementById('cl4-cta');
  if (ctaSection && docId) {
    ctaSection.classList.add('position-relative');
    ctaSection.insertAdjacentHTML('afterbegin',
      getSanityEditLink(docId, 'cloudPage', 'ctaHeadline', 'Edit CTA'));
  }
}

// ── IntersectionObserver helpers ──────────────────────────────────────────────

function attachRevealObserver(els) {
  const io = new IntersectionObserver(entries => {
    entries.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
    });
  }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
  els.forEach(el => io.observe(el));
}

function initArcStage() {
  const stage = document.getElementById('cl4-arc-stage');
  if (!stage) return;
  const io = new IntersectionObserver(entries => {
    entries.forEach(e => {
      if (e.isIntersecting) { stage.classList.add('in'); io.unobserve(stage); }
    });
  }, { threshold: 0.4 });
  io.observe(stage);
}

// ── Navbar transparency ───────────────────────────────────────────────────────

function initNavbarScroll() {
  const navbar = document.querySelector('.navbar');
  if (!navbar) return;
  function onScroll() {
    if (window.scrollY > 50) {
      navbar.classList.add('navbar-v3-scrolled');
      navbar.classList.remove('navbar-v3-transparent');
    } else {
      navbar.classList.remove('navbar-v3-scrolled');
      navbar.classList.add('navbar-v3-transparent');
    }
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

// ── Bootstrap static reveal elements ─────────────────────────────────────────

function initStaticReveal() {
  const els = document.querySelectorAll('.cl4-wrap [data-reveal]');
  attachRevealObserver(els);
  setTimeout(() => {
    document.querySelectorAll('.cl4-wrap [data-reveal]:not(.in)').forEach(el => el.classList.add('in'));
  }, 1200);
}

function initCsRail() {
  const rail = document.getElementById('cl4-cs-rail');
  const grid = document.getElementById('cl4-cs-grid');
  if (!rail || !grid) return;
  const prev = rail.querySelector('.cl4-cs-arrow.prev');
  const next = rail.querySelector('.cl4-cs-arrow.next');

  function updateArrows() {
    if (prev) prev.disabled = grid.scrollLeft <= 0;
    if (next) next.disabled = grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 1;
  }
  function checkOverflow() {
    rail.classList.toggle('has-overflow', grid.scrollWidth > grid.clientWidth);
    updateArrows();
  }
  if (prev) prev.addEventListener('click', () => {
    const card = grid.querySelector('.cl4-cs-card');
    grid.scrollBy({ left: -(card ? card.offsetWidth + 24 : 300), behavior: 'smooth' });
  });
  if (next) next.addEventListener('click', () => {
    const card = grid.querySelector('.cl4-cs-card');
    grid.scrollBy({ left: card ? card.offsetWidth + 24 : 300, behavior: 'smooth' });
  });
  grid.addEventListener('scroll', updateArrows, { passive: true });
  new ResizeObserver(checkOverflow).observe(grid);
  checkOverflow();
}

// ── Bootstrap ─────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', async () => {
  initNavbarScroll();
  initStaticReveal();
  initArcStage();
  initCsRail();

  let data;
  try {
    data = await client.fetch(QUERY);
  } catch (err) {
    console.warn('[cloud-page] Sanity fetch failed — using static HTML fallback.', err);
    return;
  }

  if (!data) {
    console.warn('[cloud-page] No Sanity document found for slug "cloud".');
    return;
  }

  const docId = data._id;

  // ── Visibility ──
  setVisible('cl4-hero',         data.showHero);
  setVisible('cl4-product-dev',  data.showProductDev);
  setVisible('cl4-consulting',   data.showConsulting);
  setVisible('cl4-case-studies', data.showCaseStudies);
  setVisible('cl4-cta',          data.showCta);

  // ── Content injection ──
  if (data.showHero !== false)        renderHero(data, docId);
  if (data.showProductDev !== false)  renderProductDev(data, docId);
  if (data.showConsulting !== false && data.consultingSection) {
    renderConsulting(data.consultingSection, docId);
  }
  if (data.showCaseStudies !== false) {
    renderCaseStudies(data.caseStudies || [], docId);
    if (data.caseStudies && data.caseStudies.length) initCsRail();
  }
  if (data.showCta !== false) renderCta(data, docId);
});
